import type { Stripe } from 'stripe';
import { clientFetch } from '~/lib/.client/fetch';
import { stripeStore } from '~/lib/stores/stripe';
import type { StripeIntegrationInfoGetResponse } from '~/routes/api.stripe.$projectId';
import type { StripeProductsPostResponse } from '~/routes/api.stripe.products.$projectId';
import type {
  StripeSecretsPostBody,
  StripeSecretsPostResponse,
  StripeSecretsGetResponse,
} from '~/routes/api.stripe.secrets.$projectId';
import type {
  StripeWebhookGetResponse,
  StripeWebhookPostBody,
  StripeWebhookPostResponse,
} from '~/routes/api.stripe.webhook.$projectId';
import { hidePartOfApiKey } from '~/utils/stripe-helpers';

export async function checkStripeSecretKeyAndListProducts(
  projectId: string,
  secretKey: string,
): Promise<Array<Stripe.Product>> {
  await saveSecretKey(projectId, secretKey);

  try {
    const products = await listProducts(projectId);

    stripeStore.partialKey.set(hidePartOfApiKey(secretKey));

    return products;
  } catch (error) {
    await deleteSecretKey(projectId);

    throw error;
  }
}

export async function listProducts(projectId: string): Promise<Array<Stripe.Product>> {
  const response = await clientFetch(`/api/stripe/products/${projectId}`, {
    method: 'POST',
  });

  const result = await response.json<StripeProductsPostResponse>();

  if (result && result.success) {
    return result.products;
  }

  throw new Error(result?.error ?? 'An unknown error occurred.');
}

export async function getStripeIntegrationInfo(projectId: string) {
  const response = await clientFetch(`/api/stripe/${projectId}`);

  const result = await response.json<StripeIntegrationInfoGetResponse>();

  if (result && result.success) {
    return result.integrationInfo;
  }

  throw new Error(result?.error ?? 'An unknown error occurred.');
}

export async function saveSecretKey(projectId: string, secretKey: string): Promise<void> {
  const response = await clientFetch(`/api/stripe/secrets/${projectId}`, {
    method: 'POST',
    body: JSON.stringify({ secretKey } satisfies StripeSecretsPostBody),
  });

  const result = await response.json<StripeSecretsPostResponse>();

  if (result && result.success) {
    return;
  }

  throw new Error(result?.error ?? 'An unknown error occurred.');
}

export async function deleteSecretKey(projectId: string): Promise<void> {
  try {
    await clientFetch(`/api/stripe/secrets/${projectId}`, {
      method: 'DELETE',
    });
  } catch {
    // do nothing
  }
}

export async function getPartialSecretKey(projectId: string): Promise<string | undefined> {
  const response = await clientFetch(`/api/stripe/secrets/${projectId}`);

  const body = await response.json<StripeSecretsGetResponse>();

  if (body && body.success) {
    return body.partialKey;
  }

  throw new Error(body?.error ?? 'An unknown error occurred.');
}

/**
 * !! IMPORTANT !!
 *
 * This API endpoint GOES to stripe. So use CAREFULLY. Make sure this is NEVER
 * called as part of the rendering lifecycle of a component.
 */
export async function getStripeWebhookInfo(projectId: string) {
  const response = await clientFetch(`/api/stripe/webhook/${projectId}`);

  const body = await response.json<StripeWebhookGetResponse>();

  if (body && body.success) {
    return body.webhooks;
  }

  throw new Error(body?.error ?? 'An unknown error occurred.');
}

export async function createWebhookOnStripeAndUpdateSecretToSupabase(projectId: string, edgeFunctionSlug: string) {
  const response = await clientFetch(`/api/stripe/webhook/${projectId}`, {
    method: 'POST',
    body: JSON.stringify({ edgeFunctionSlug } satisfies StripeWebhookPostBody),
  });

  const body = await response.json<StripeWebhookPostResponse>();

  if (body && body.success) {
    return;
  }

  throw new Error(body?.error ?? 'An unknown error occurred.');
}
